import React from "react";
import PropTypes from "prop-types";
import { useForm, Controller } from "react-hook-form";
import styled from "styled-components";
import chroma from "chroma-js";

import { createAccount } from "../../configureStore/user";
import { colors, GridWrapper, TextField, Button } from "../../theme";
import { SupportContainer } from "./supportTheme";
import LoginForm from "../LoginForm";
import OverlayLoader from "../loader/OverlayLoader";

const Container = styled(SupportContainer)`
  padding: 70px 0;

  @media (max-width: 1023px) {
    padding: 40px 0 70px;
  }
`;

const Main = styled(GridWrapper)`
  align-items: center;
  background: white;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  display: flex;
  margin-top: 46px;
  max-width: 890px;
  position: relative;
  z-index: 1;

  @media (max-width: 1023px) {
    display: block;
    margin-top: 32px;
  }

  section {
    padding: 50px 80px;
    width: 50%;

    @media (max-width: 1023px) {
      width: auto;
    }

    @media (max-width: 767px) {
      padding: 30px 20px;
    }

    :first-of-type {
      border-right: 1px solid ${chroma(colors.purple).alpha(0.3)};
      position: relative;

      @media (max-width: 1023px) {
        border: 0;
        border-bottom: 1px solid ${chroma(colors.purple).alpha(0.3)};
        padding-bottom: 70px;
      }

      :after {
        background: ${colors.purple};
        border-radius: 50%;
        color: white;
        content: "ou";
        font-size: 1.6rem;
        font-weight: 500;
        display: block;
        height: 46px;
        line-height: 4.6rem;
        position: absolute;
        right: -23px;
        top: 50%;
        transform: translateY(-50%);
        width: 46px;

        @media (max-width: 1023px) {
          bottom: -23px;
          right: 50%;
          top: auto;
          transform: translateX(50%);
        }
      }
    }

    :last-of-type {
      @media (max-width: 1023px) {
        padding-top: 70px;
      }
    }
  }

  h2 {
    color: black;
    font-weight: 500;
    font-size: 2.8rem;
    letter-spacing: -0.02rem;
    margin-bottom: 35px;
    text-transform: uppercase;

    @media (max-width: 1023px) {
      font-size: 2rem;
      margin-bottom: 25px;
    }
  }

  form > div:last-child {
    margin-top: 45px;

    @media (max-width: 767px) {
      margin-top: 35px;
    }

    a {
      color: ${colors.purple};
      font-weight: 500;
      margin-left: 20px;
      text-decoration: none;
      transition: 0.3s;

      &:hover {
        color: ${colors.red};
      }

      @media (max-width: 767px) {
        margin-left: 0;
        margin-top: 25px;
        display: block;
      }
    }
  }
`;

function SupportAccount({ dispatch, account }) {
  const { errors, handleSubmit, getValues, control } = useForm({
    defaultValues: {
      email: "",
      screenname: "",
      password: "",
      passwordValidation: "",
    },
  });

  function onSubmit(data) {
    dispatch(createAccount(data));
  }

  return (
    <Container>
      <h1>
        <span>1.</span> S'enregistrer
      </h1>
      <p>
        Pour commencer, créez votre compte ou connectez-vous. Il vous permettra
        de rester en contact avec Le Média et de suivre l'état de votre
        abonnement.
      </p>
      <Main>
        <OverlayLoader
          isVisible={account.isFetching}
          error={account.error != null}
        />
        <section>
          <h2>Je m'inscris</h2>
          <form onSubmit={handleSubmit(onSubmit)}>
            <fieldset>
              <Controller
                as={TextField}
                label="Adresse e-mail"
                type="email"
                name="email"
                control={control}
                required
                rules={{
                  required: "L'adresse e-mail est requise.",
                  pattern: {
                    value: /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    message: "L'adresse e-mail doit être valide.",
                  },
                }}
                error={errors.email !== undefined}
                helperText={errors.email && errors.email.message}
              />
            </fieldset>
            <fieldset>
              <Controller
                as={TextField}
                name="screenname"
                control={control}
                label="Nom visible publiquement"
                required
                rules={{
                  required: "Le nom visible est requis.",
                  minLength: {
                    value: 3,
                    message:
                      "Votre nom visible doit faire au moins 3 caractères.",
                  },
                }}
                error={errors.screenname !== undefined}
                helperText={errors.screenname && errors.screenname.message}
              />
            </fieldset>
            <fieldset>
              <Controller
                as={TextField}
                label="Mot de passe"
                type="password"
                name="password"
                control={control}
                required
                rules={{
                  required: "Le mot de passe est requis.",
                  minLength: {
                    value: 6,
                    message:
                      "Votre mot de passe doit faire au moins 6 caractères.",
                  },
                }}
                error={errors.password !== undefined}
                helperText={errors.password && errors.password.message}
              />
            </fieldset>
            <fieldset>
              <Controller
                as={TextField}
                label="Confirmation du mot de passe"
                type="password"
                name="passwordValidation"
                control={control}
                required
                rules={{
                  required: "Le mot de passe est requis.",
                  minLength: {
                    value: 6,
                    message:
                      "Votre mot de passe doit faire au moins 6 caractères.",
                  },
                  validate: (value) => getValues().password === value,
                }}
                error={errors.passwordValidation !== undefined}
                helperText={
                  errors.passwordValidation &&
                  (errors.passwordValidation.message ||
                    (errors.passwordValidation.type === "validate" &&
                      "Les mots de passe ne correspondent pas."))
                }
              />
            </fieldset>
            {account.error && <p className="error">{account.error}</p>}
            <div>
              <Button type="submit">Créer mon compte</Button>
            </div>
          </form>
        </section>
        <LoginForm hideFetchingOverlay />
      </Main>
    </Container>
  );
}

SupportAccount.propTypes = {
  dispatch: PropTypes.func.isRequired,
  account: PropTypes.shape({
    isFetching: PropTypes.bool.isRequired,
    error: PropTypes.string,
  }).isRequired,
};

export default SupportAccount;
